import React, { useState, useEffect } from 'react'
import './unifiedUI.scss'
import infoData from './infoData.json'
import appData from './appData.json'

// Function to extract all image paths from the JSON data
function extractImagePaths(data) {
  const imagePaths = new Set()
  
  // Recursive function to find all image properties
  function findImages(obj) {
    if (typeof obj === 'string' && (obj.includes('.webp') || obj.includes('.jpg') || obj.includes('.png') || obj.includes('.svg'))) {
      imagePaths.add(obj)
    } else if (typeof obj === 'object' && obj !== null) {
      Object.values(obj).forEach(findImages)
    } else if (Array.isArray(obj)) {
      obj.forEach(findImages)
    }
  }
  
  findImages(data)
  return Array.from(imagePaths)
}

// Function to preload images
function preloadImages(imagePaths) {
  return Promise.all(
    imagePaths.map(
      (src) =>
        new Promise((resolve, reject) => {
          const img = new Image()
          img.onload = () => resolve(src)
          img.onerror = () => {
            console.warn(`Failed to load image: ${src}`)
            resolve(src) // Resolve anyway to not block the loading
          }
          img.src = src
        })
    )
  )
}

// Function to validate content structure
function validateContentStructure(data) {
  try {
    console.log('Validating content structure...')
    
    // Check if main sections exist
    if (!data.gallery?.projects) {
      throw new Error('Gallery projects data missing')
    }
    
    if (!data.gallery?.clientPreviews) {
      throw new Error('Gallery clientPreviews data missing')
    }
    
    // Validate projects array
    if (!Array.isArray(data.gallery.projects)) {
      throw new Error('Gallery projects is not an array')
    }
    
    // Validate each project has required properties
    for (const project of data.gallery.projects) {
      if (!project.id || !project.title) {
        console.warn(`Project missing id or title: ${project.id || 'unknown'}`)
      }
      
      // Check projectPart structure if it exists
      if (project.projectPart) {
        for (const [partKey, part] of Object.entries(project.projectPart)) {
          if (!part.id || !part.description) {
            console.warn(`Project part ${partKey} missing required fields in ${project.title}`)
          }
        }
      }
    }
    
    // Validate clientPreviews
    for (const client of data.gallery.clientPreviews) {
      if (!client.id || !client.clientName) {
        console.warn(`Client preview missing required fields`)
      }
    }
    
    console.log('Content structure validation completed')
    return true
  } catch (error) {
    console.error('Content structure validation failed:', error)
    return false
  }
}

export function LoadingScreen({ onStartExperience, sceneReady, userMadeChoice, onImagesReady }) {
  const { loadingScreen } = appData
  const [imagesReady, setImagesReady] = useState(false)
  const [contentValidated, setContentValidated] = useState(false)
  const [loadingProgress, setLoadingProgress] = useState(0)
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState(false)
  const [contentFullyLoaded, setContentFullyLoaded] = useState(false)
  const [showWelcome, setShowWelcome] = useState(false)
  
  console.log('LoadingScreen render:', { 
    sceneReady, 
    imagesReady, 
    contentValidated, 
    minimumTimeElapsed, 
    contentFullyLoaded,
    showWelcome,
    userMadeChoice
  })
  
  // Preload images and manage minimum display time
  useEffect(() => {
    console.log('Starting loading sequence...')
    const MINIMUM_LOADING_TIME = 3000 // 3 seconds minimum
    
    // Validate content structure first
    const contentIsValid = validateContentStructure(infoData)
    console.log('Content validation result:', contentIsValid)
    setContentValidated(contentIsValid)
    
    // Start minimum time countdown
    const minimumTimeTimer = setTimeout(() => {
      console.log('Minimum loading time elapsed')
      setMinimumTimeElapsed(true)
    }, MINIMUM_LOADING_TIME)
    
    // Start image preloading
    const imagePaths = extractImagePaths(infoData)
    console.log(`Found ${imagePaths.length} images to preload`)
    
    let loadedCount = 0
    const totalImages = imagePaths.length
    
    const loadWithProgress = imagePaths.map(
      (src) =>
        new Promise((resolve) => {
          const img = new Image()
          img.onload = img.onerror = () => {
            loadedCount++
            const progress = (loadedCount / totalImages) * 100
            setLoadingProgress(progress)
            resolve(src)
          }
          img.src = src
        })
    )
    
    Promise.all(loadWithProgress).then(() => {
      console.log('All images preloaded')
      setImagesReady(true)
      onImagesReady?.()
    })
    
    return () => clearTimeout(minimumTimeTimer)
  }, [onImagesReady])
  
  // Check if content is fully loaded (all conditions met)
  useEffect(() => {
    console.log('Checking loading conditions:', {
      sceneReady,
      imagesReady, 
      contentValidated,
      minimumTimeElapsed
    })
    
    if (sceneReady && imagesReady && contentValidated && minimumTimeElapsed) {
      console.log('All conditions met - content fully loaded!')
      setContentFullyLoaded(true)
      // Small delay before showing welcome to allow for smooth transition
      setTimeout(() => {
        console.log('Showing welcome screen')
        setShowWelcome(true)
      }, 200)
    }
  }, [sceneReady, imagesReady, contentValidated, minimumTimeElapsed])
  
  const handleChoice = (withAudio) => {
    if (contentFullyLoaded) {
      onStartExperience(withAudio)
    }
  }

  // Show loading indicator until content is fully loaded
  const isLoading = !contentFullyLoaded
  const canInteract = contentFullyLoaded && showWelcome
  
  console.log('Render logic:', { isLoading, canInteract, contentFullyLoaded, showWelcome })

  return (
    <div className="loading-screen">
      <div className="loading-content">
        {isLoading ? (
          <div className="loading-indicator">
            <div className="circular-progress">
              <svg className="progress-ring" width="120" height="120">
                <circle
                  className="progress-ring-circle-bg"
                  cx="60"
                  cy="60"
                  r="54"
                />
                <circle
                  className="progress-ring-circle"
                  cx="60"
                  cy="60"
                  r="54"
                  style={{
                    strokeDasharray: `${2 * Math.PI * 54}`,
                    strokeDashoffset: `${2 * Math.PI * 54 * (1 - loadingProgress / 100)}`
                  }}
                />
              </svg>
              <div className="progress-text">
                <span className="progress-number">{Math.round(loadingProgress)}</span>
                <span className="progress-percent">%</span>
              </div>
            </div>
            <p className="text-body-large">Loading experience...</p>
          </div>
        ) : showWelcome ? (
          <>
            <div>
              {loadingScreen.welcomeMessage.map((message, index) => (
                <div key={index}>
                  <div className='text-box'>
                    <h3 className="text-heading-1">{message.title}</h3>
                    <p className="text-body-large">{message.body}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="response-options">
              {loadingScreen.choices.map((choice) => {
                return (
                  <button 
                    key={choice.id}
                    className={`response-option ${!canInteract ? 'disabled' : ''}`}
                    onClick={() => handleChoice(choice.value)}
                    disabled={!canInteract}
                  >
                    <div className="response-header">
                      <h4 className="text-heading-2">{choice.heading}</h4>
                      <div className="btn-base round">
                        {choice.value ? (
                          <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" fill="currentColor">
                            <path d="M560-131v-82q90-26 145-100t55-168q0-94-55-168T560-749v-82q124 28 202 125.5T840-481q0 127-78 224.5T560-131ZM120-360v-240h160l200-200v640L280-360H120Zm440 40v-322q47 22 73.5 66t26.5 96q0 51-26.5 94.5T560-320ZM400-606l-86 86H200v80h114l86 86v-252ZM300-480Z"/>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" fill="currentColor">
                            <path d="M792-56 671-177q-25 16-53 27.5T560-131v-82q14-5 27.5-10t25.5-12L480-368v208L280-360H120v-240h128L56-792l56-56 736 736-56 56Zm-8-232-58-58q17-31 25.5-65t8.5-70q0-94-55-168T560-749v-82q124 28 202 125.5T840-481q0 53-14.5 102T784-288ZM650-422l-90-90v-130q47 22 73.5 66t26.5 96q0 15-2.5 29.5T650-422ZM480-592 376-696l104-104v208Zm-80 238v-94l-72-72H200v80h114l86 86Zm-36-130Z"/>
                          </svg>
                        )}
                      </div>
                    </div>
                    {choice.paragraph && <p className="text-body">{choice.paragraph}</p>}
                  </button>
                )
              })}
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}